import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useAppSelector } from '../../../store';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useTranslations from '../../../hooks/useTranslations';
import useGetGridData from '../../../hooks/useGetGridData';
import CheckIcon from '@mui/icons-material/Check';
import ActionButtons from '../../../components/UI/ActionButtons/ActionButtons';
import { ISortItem } from '../../../models/table';
import {
  sortDataByDate,
  getDateTimeAndSeconds,
} from '../../../utils/formatters';
import useUrlSearchParam from '../../../hooks/useUrlSearchParams';
import { FieldTypes, IFormFields } from '../../../models/form';
import Filters from '../../../components/Container/Filters/Filters';
import { customerListSearchValidation } from '../../../utils/formValidation/customerList';
import { CustomersActions } from '../../../store/entities/customers/customers.actions';
import { ActionPermissionsActions } from '../../../store/entities/actionPermissions/actionPermissions.actions';
import Title from '../../../components/UI/Title/Title';
import { CONSTANTS } from '../../../utils/constants';
import { PERMISSIONS } from '../../../utils/permissions';
import { IPage } from '../../../models/page';
import EnhancedTable from '../../../components/UI/RcTable/EnhancedTable';
import Alert from '../../../components/UI/Alert/Alert';

import './CustomerList.scss';
import { TableActions } from '../../../store/entities/table/table.actions';
import TableButtons from '../../../components/UI/TableButtons/TableButtons';
import { IButton } from '../../../models/button';
import StatusDot from '../../../components/UI/StatusDot/StatusDot';
import CSVLinkContainer from '../../../components/Container/CSVLinkContainer/CSVLinkContainer';
import { ConfigurationActions } from '../../../store/entities/configuration/configuration.actions';
import { GeneralActions } from '../../../store/entities/general/general.actions';
import { SitesActions } from '../../../store/entities/sites/sites.actions';
import { ISite, ISiteType } from '../../../models/site';
import { getCustomerSite } from '../../../utils/addressFormatters';

const CustomerList = ({ permission, nextRoute }: IPage) => {
  const [rows, setRows] = useState<any[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertColor, setAlertColor] = useState<'success' | 'error'>('success');

  const showAlert = (message: string, color: 'success' | 'error' = 'success') => {
    setAlertMessage(message);
    setAlertColor(color);
    setShow(true);
    setTimeout(() => setShow(false), 5000);
  };

  const dispatch = useDispatch();
  const { rcTranslate } = useTranslations();
  const { onPageChange } = useGetGridData();
  const navigate = useNavigate();
  const { getValueFromQuery, params } = useUrlSearchParam();

  const { totalRows, queryParams, nextPageNumber, currentPage, sortModel } =
    useAppSelector((state) => state.table);
  const { customers } = useAppSelector((state) => state.customers);
  const { appPermissions }: { appPermissions: string[] } = useAppSelector(
    (store) => store.auth
  );
  const { formikProps } = useAppSelector((store) => store.form);
  const { orderStatuses, appLanguage } = useAppSelector(
    (store) => store.general
  );
  const { siteListDetails, siteTypes } = useAppSelector((store) => store.sites);

  useEffect(() => {
    if (permission === false) {
      navigate(nextRoute);
    }
  }, [permission, nextRoute, navigate]);

  useEffect(() => {
    if (permission) {
      if (currentPage !== 1) {
        dispatch(TableActions.setTableData({ currentPage: 1 }));
      }
      dispatch(CustomersActions.getCustomers(params));
      dispatch(GeneralActions.getOrderStatuses());
      dispatch(SitesActions.getSiteListDetails());
      dispatch(SitesActions.getSiteTypes());
    }
  }, [dispatch, params, permission]);

  useEffect(() => {
    if (nextPageNumber) {
      const getData = async () => {
        dispatch(
          CustomersActions.getCustomers(
            params,
            sortModel,
            false,
            nextPageNumber
          )
        );
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [dispatch, nextPageNumber, sortModel]);

  useEffect(() => {
    if (sortModel.length) {
      const getData = async () => {
        dispatch(CustomersActions.getCustomers(null, sortModel, false));
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [sortModel]);

  const columns: any[] = useMemo(
    () => [
      {
        field: 'dotStatus',
        headerName: '',
        headerClassName:
          'hideColumnSeperator manageReports__dotStatus manageOrders_columnHeader',
        sortable: false,

        id: 'dotStatus',
        numeric: false,
        disablePadding: true,
        label: '   ',
      },
      {
        id: 'firstName',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customers.filters.firstName'),
      },
      {
        id: 'lastName',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customers.filters.lastName'),
      },
      {
        id: 'phoneNumber',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customers.filters.phoneNumber'),
      },
      {
        id: 'identificationNumber',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customers.filters.identity'),
      },
      {
        id: 'email',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customers.filters.email'),
      },
      {
        id: 'mailSubscription',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customers.filters.mailing'),
      },
      {
        id: 'created',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customers.filters.registrationDate'),
      },
      {
        id: 'lastOrderDate',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customers.filters.lastOrderDate'),
      },
      {
        id: 'orderId',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customers.filters.orderId'),
      },
      {
        id: 'orderStatus',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('orders.filters.orderStatus'),
      },
      {
        id: 'cellNumber',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('orders.orderHistory.cellNumber'),
      },
      {
        id: 'hasFullAccessPermission',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('customers.filters.hasFullAccessPermission'),
        sortable: false,
      },
       {
        id: 'preferredSite',
        disablePadding: true,
        label: rcTranslate('customers.filters.preferredSiteId'),
      },
      {
        id: 'preferredSiteTypeName',
        disablePadding: true,
        label: rcTranslate('customers.filters.preferredSiteTypeName'),
      },
      {
        id: 'deliveriesWithoutPictureCount',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('customers.filters.deliveriesWithoutPictureCount'),
        sortable: false,
      },
    ],
    [rcTranslate]
  );

  const naviagteToCustomerDetails = useCallback(
    (customerId: string) => {
      navigate(
        '/' +
          CONSTANTS.ROUTES.CUSTOMERS.BASE +
          '/' +
          CONSTANTS.ROUTES.CUSTOMERS.CHILDREN.CUSTOMER_DETAILS +
          `/${customerId}`
      );
    },
    [navigate]
  );

  const naviagteToCustomerOrders = useCallback(
    (customerId: string) => {
      navigate(
        '/' +
          CONSTANTS.ROUTES.ORDERS_MANAGEMENT.BASE +
          '/' +
          CONSTANTS.ROUTES.ORDERS_MANAGEMENT.CHILDREN.ORDERS_LIST +
          `/?CustomerId=${customerId}`,
        { state: { customerId } }
      );
    },
    [navigate]
  );

  const canAddFullPermission = appPermissions.includes(PERMISSIONS.ADD_CUSTOMER_FULL_ACTION_PERMISSION);
  const canUpdatePermission = appPermissions.includes(PERMISSIONS.UPDATE_ACTION_PERMISSION);
  const canResetCourierDeliveriesWithoutPictureCount = appPermissions.includes(PERMISSIONS.RESET_COURIER_DELIVERIES_WITHOUT_PICTURE_COUNT);

  const handleAddFullAccessPermission = useCallback(
    async (customerId: string) => {
      const hasActiveOrders = await dispatch(CustomersActions.getHasActiveResidentialOrders(customerId));
      if (!hasActiveOrders) {
        showAlert('customers.customerList.noActiveResidentialOrders', 'error');
        return;
      }
      const result = await dispatch(ActionPermissionsActions.addCustomerFullActionPermission(customerId));
      if (result != null) {
        dispatch(CustomersActions.getCustomers(params));
        showAlert('customers.customerList.addFullAccessPermissionSuccess');
      }
    },
    [dispatch, params]
  );

  const handleCancelFullAccessPermission = useCallback(
    async (actionPermissionId: number) => {
      const result = await dispatch(ActionPermissionsActions.updateActionPermission(actionPermissionId, { status: 'Cancelled' }));
      if (result !== undefined) {
        dispatch(CustomersActions.getCustomers(params));
        showAlert('customers.customerList.cancelFullAccessPermissionSuccess');
      }
    },
    [dispatch, params]
  );

  const handleResetCourierDeliveriesWithoutPictureCount = useCallback(
    async (customerId: string) => {
      const result = await dispatch(CustomersActions.resetCourierDeliveriesWithoutPictureCount(customerId));
      if (result != null) {
        dispatch(CustomersActions.getCustomers(params));
        showAlert('customers.customerList.resetCourierDeliveriesWithoutPictureCountSuccess');
      }
    },
    [dispatch, params]
  );

  const sendMessage = () => {};

  // Map dynamic list options for ORDER_STATUSES
  const orderStatusesMap = useMemo(() => {
    const map: Record<number, string> = {};
    orderStatuses?.forEach((orderStatus: any) => {
      if (orderStatus) {
        map[orderStatus.id] =
          (appLanguage === 'hebrew'
            ? orderStatus.nameHE
            : orderStatus.nameEN) ?? '';
      }
    });
    return map;
  }, [orderStatuses, appLanguage]);

  const mapCustomersToTableRows = useCallback(
    (customers: any[]) => {
      return customers.map((customer, index) => {
        const customerObject: any = {
          id: index,
          statusDot: (
            <StatusDot
              className="tableRow__statusDot"
              status={customer.isActive ? 'active' : 'inactive'}
            />
          ),
          Id: customer.customerId,
          firstName: customer.firstName,
          lastName: customer.lastName,
          phoneNumber: customer.phoneNumber,
          identificationNumber: customer.identificationNumber,
          email: customer.email,
          mailSubscription: customer.mailSubscription ? <CheckIcon /> : '',
          created: getDateTimeAndSeconds(customer.created),
          lastOrderDate: customer.lastOrderDate
            ? getDateTimeAndSeconds(customer.lastOrderDate)
            : '',
          orderId: customer.orderId ?? '',
          orderStatus: Boolean(customer.orderStatus)
            ? (orderStatusesMap[customer.orderStatus] ?? customer.orderStatus)
            : '',
          cellNumber: customer.cellNumber ?? '',
          hasFullAccessPermission: customer.fullAccessPermissionId != null ? <div style={{ display: 'flex', justifyContent: 'center' }}><CheckIcon /></div> : '',
          preferredSite: customer.preferredSiteId
            ? getCustomerSite(customer.preferredSiteId, siteListDetails, appLanguage)
            : '',
          preferredSiteTypeName: customer.preferredSiteId
            ? (siteTypeNameBySiteId.current.get(customer.preferredSiteId) ?? '')
            : '',
          deliveriesWithoutPictureCount: customer.deliveriesWithoutPictureCount ?? 0,
          actions: (
            <ActionButtons
              style={{ marginRight: 'auto' }}
              hide={
                [
                  PERMISSIONS.GET_CUSTOMER_DETAILS,
                  PERMISSIONS.GET_ORDERS,
                  PERMISSIONS.ADD_CUSTOMER_FULL_ACTION_PERMISSION,
                  PERMISSIONS.UPDATE_ACTION_PERMISSION,
                ].filter((v: string) => appPermissions.includes(v)).length === 0
              }
              menuItems={[
                {
                  label: 'customers.customerList.actions.customerDetails',
                  onClick: () =>
                    naviagteToCustomerDetails(customer?.customerId?.toString()),
                  show: appPermissions.includes(
                    PERMISSIONS.GET_CUSTOMER_DETAILS
                  ),
                },
                {
                  label: 'customers.customerList.actions.orderManagement',
                  onClick: () =>
                    naviagteToCustomerOrders(customer?.customerId?.toString()),
                  show: appPermissions.includes(PERMISSIONS.GET_ORDERS),
                },
                {
                  label: 'customers.customerList.actions.sendMessage',
                  onClick: sendMessage,
                  // show: !appPermissions.includes(PERMISSIONS.GET_CUSTOMER_DETAILS)
                },
                {
                  label: 'customers.customerList.actions.addFullAccessPermission',
                  onClick: () => handleAddFullAccessPermission(customer?.customerId?.toString()),
                  show: canAddFullPermission && customer.fullAccessPermissionId == null,
                },
                {
                  label: 'customers.customerList.actions.cancelFullAccessPermission',
                  onClick: () => handleCancelFullAccessPermission(customer.fullAccessPermissionId),
                  show: canUpdatePermission && customer.fullAccessPermissionId != null,
                },
                {
                  label: 'customers.customerList.actions.resetCourierDeliveriesWithoutPictureCount',
                  onClick: () => handleResetCourierDeliveriesWithoutPictureCount(customer?.customerId?.toString()),
                  show: canResetCourierDeliveriesWithoutPictureCount && customer.deliveriesWithoutPictureCount > 0,
                },
              ]}
            />
          ),
        };

        return customerObject;
      });
    },
    [
      rows,
      customers,
      appPermissions,
      naviagteToCustomerDetails,
      naviagteToCustomerOrders,
      orderStatusesMap,
      canAddFullPermission,
      canUpdatePermission,
      canResetCourierDeliveriesWithoutPictureCount,
      handleAddFullAccessPermission,
      handleCancelFullAccessPermission,
      handleResetCourierDeliveriesWithoutPictureCount,
    ]
  );

  const siteTypeNameBySiteId = useRef(new Map<number, string>());

  useEffect(() => {
    if (!customers?.length || !siteListDetails?.length || !siteTypes?.length) return;
    const siteTypesMap = new Map(siteTypes.map((t: ISiteType) => [t.id, t]));
    customers.forEach((customer) => {
      if (!customer.preferredSiteId || siteTypeNameBySiteId.current.has(customer.preferredSiteId)) return;
      const site = siteListDetails.find((s: ISite) => s.id === customer.preferredSiteId);
      if (!site?.siteTypeId) return;
      const siteType = siteTypesMap.get(site.siteTypeId);
      if (siteType) {
        siteTypeNameBySiteId.current.set(
          customer.preferredSiteId,
          (appLanguage === CONSTANTS.DEFAULT_LANGUAGE
            ? siteType.nameHE : siteType.nameEN)
            ?? ''
        );
      }
    });
  }, [customers, siteListDetails, siteTypes, appLanguage, mapCustomersToTableRows]);

  const siteSelectItems = useMemo(
    () =>
      siteListDetails?.map((s: ISite) => ({
        key: String(s.id),
        value: appLanguage === CONSTANTS.DEFAULT_LANGUAGE ? s.nameHE : s.nameEN,
      })) ?? [],
    [siteListDetails, appLanguage]
  );

  useEffect(() => {
    if (!customers?.length) return setRows([]);
    const customersRows = mapCustomersToTableRows(customers);
    setRows(customersRows);
  }, [customers, appPermissions]);

  const searchFields: IFormFields = {
    initialValues: {
      firstName: getValueFromQuery('firstName'),
      lastName: getValueFromQuery('lastName'),
      identificationNumber: getValueFromQuery('identificationNumber'),
      phoneNumber: getValueFromQuery('phoneNumber'),
      customerId: getValueFromQuery('customerId'),
      email: getValueFromQuery('email'),
      mailSubscription: getValueFromQuery('mailSubscription'),
      fromCreatedDate: getValueFromQuery('fromCreatedDate'),
      toCreatedDate: getValueFromQuery('toCreatedDate'),
      fromLastOrderDate: getValueFromQuery('fromLastOrderDate'),
      toLastOrderDate: getValueFromQuery('toLastOrderDate'),
      preferredSiteId: getValueFromQuery('preferredSiteId'),
      minDeliveriesWithoutPictureCount: getValueFromQuery('minDeliveriesWithoutPictureCount'),
    },
    formData: [
      {
        type: FieldTypes.CHILDREN,
        id: 'externalFieldsWrapper',
        fields: [
          {
            type: FieldTypes.TEXT,
            id: 'firstName',
            label: 'searchFields.firstName',
          },
          {
            type: FieldTypes.TEXT,
            id: 'lastName',
            label: 'searchFields.lastName',
          },
          {
            type: FieldTypes.TEXT,
            id: 'identificationNumber',
            props: { inputProps: { maxLength: 9 } },
            label: 'searchFields.identity',
          },
          {
            type: FieldTypes.TEL,
            id: 'phoneNumber',
            label: 'searchFields.phoneNumber',
            props: { inputProps: { maxLength: 10 } },
          },
        ],
      },
      {
        type: FieldTypes.CHILDREN,
        id: 'extendedFieldsWrapper',
        fields: [
          {
            type: FieldTypes.CHILDREN,
            id: 'extendedFieldsContainer',
            fields: [
              {
                type: FieldTypes.CHILDREN,
                id: 'extendedFields',
                className: 'filters__extendedFields',
                fields: [
                  {
                    type: FieldTypes.TEXT,
                    id: 'customerId',
                    label: 'searchFields.customerId',
                  },
                  {
                    type: FieldTypes.TEXT,
                    id: 'email',
                    label: 'searchFields.email',
                  },
                  {
                    type: FieldTypes.SELECT,
                    id: 'mailSubscription',
                    label: 'searchFields.mailing.label',
                    items: [
                      {
                        key: 'true',
                        value: rcTranslate('searchFields.mailing.items.yes'),
                      },
                      {
                        key: 'false',
                        value: rcTranslate('searchFields.mailing.items.no'),
                      },
                    ],
                  },
                  {
                    type: FieldTypes.DATE,
                    id: 'fromCreatedDate',
                    label: 'searchFields.fromCreatedDate',
                    props: { getStartOfDay: true },
                  },
                  {
                    type: FieldTypes.DATE,
                    id: 'toCreatedDate',
                    label: 'searchFields.toCreatedDate.label',
                    props: {
                      getFullDayTime: true,
                      minDate: new Date(formikProps?.values?.fromCreatedDate),
                    },
                  },
                  {
                    type: FieldTypes.DATE,
                    id: 'fromLastOrderDate',
                    label: 'searchFields.fromLastOrderDate',
                    props: { getStartOfDay: true },
                  },
                  {
                    type: FieldTypes.DATE,
                    id: 'toLastOrderDate',
                    label: 'searchFields.toLastOrderDate.label',
                    props: {
                      getFullDayTime: true,
                      numOfDays: 1,
                      minDate: new Date(formikProps?.values?.fromLastOrderDate),
                    },
                  },
                  {
                    type: FieldTypes.SELECT,
                    id: 'preferredSiteId',
                    label: 'customers.filters.preferredSiteId',
                    items: siteSelectItems,
                  },
                  {
                    type: FieldTypes.NUMBER,
                    id: 'minDeliveriesWithoutPictureCount',
                    label: 'customers.filters.minDeliveriesWithoutPictureCount',
                    props: { inputProps: { min: 0 } },
                  },
                ],
              },
              {
                type: FieldTypes.CHILDREN,
                id: 'actionsContainer',
                className: 'filters__extendedActions',
                fields: [
                  {
                    type: FieldTypes.BUTTON,
                    id: 'closeExtendedSearch',
                    label: 'searchFields.closeExtendedSearch',
                  },
                  {
                    type: FieldTypes.CHILDREN,
                    id: 'submitButtonWrapper',
                    className: 'filters__submitButtonWrapper',
                    fields: [
                      {
                        type: FieldTypes.SUBMIT_BUTTON,
                        id: 'submitButton',
                        label: '',
                      },
                      {
                        type: FieldTypes.BUTTON,
                        id: 'cleanSearch',
                        label: 'searchFields.cleanSearch',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  const tableButtons: IButton[] = [
    {
      title: 'locks.locksManagement.action',
      show: true,
      renderComponent: (
        <CSVLinkContainer
          columns={columns}
          getData={async () =>
            dispatch(
              CustomersActions.getCustomers(params, null, false, 1, totalRows)
            )
          }
          mapData={mapCustomersToTableRows}
          disabled={totalRows === 0}
          fileNamePrefix={'Customers_List'}
        />
      ),
    },
  ];

  const handleSortChange = (model: ISortItem[]) => {
    dispatch(CustomersActions.getCustomers(queryParams, model));
  };

  return (
    <>
      <Title text="customers.pageTitle" />

      <Filters
        fields={searchFields}
        filtersValidation={customerListSearchValidation}
      />

      <TableButtons generalButtons={[]} tableButtons={tableButtons} />

      <div className="customersList">
        <EnhancedTable
          rows={rows}
          columns={columns}
          sortDataByDate={sortDataByDate}
          checkboxSelection={true}
          expandable={false}
          showId={false}
        />
      </div>

      {show && <Alert message={rcTranslate(alertMessage)} color={alertColor} />}
    </>
  );
};

export default CustomerList;
